/**
 * E-PR (docs/fibtec-etlgen-e-pr-design-spec.md §4/§7 step 5) — a
 * "Connect GitHub" link plus a small form that delivers a completed
 * /generate job's own script (+ tests, if present) as a pull request,
 * instead of (or alongside) the existing download buttons. Design
 * spec §0: this is an alternative delivery mode for content the portal
 * already holds in memory from the preceding /generate response, not
 * a new generation call — `files` here is built directly from the
 * script/tests already on screen, matching the design spec's own
 * "script -> etl_job.py; tests, if present -> test_etl_job.py" note.
 *
 * A fourth gap found live: "Deliver as PR" stayed grayed out with no
 * visible reason once "Connect GitHub" had already been used (a prior
 * install is just a no-op redirect to GitHub's own already-installed
 * page — it doesn't touch this form at all) — the actual cause was an
 * empty "Repo (owner/name)" field, the only one of the three with no
 * default value, easy to miss since "Base branch"/"PR title" already
 * show real-looking text. Fixed with a hint naming the still-empty
 * field(s) directly, rather than leaving `ready` as a silent gate.
 *
 * A fifth gap, found in the same round: once an org IS connected,
 * retyping its own repo name by hand is pointless friction — the
 * installation already knows exactly which repos it covers
 * (`GET /github/repos`, api/lib/github_app.py's own
 * `list_installation_repositories`). "Repo" is now a dropdown of that
 * list instead of free text. Fetched on mount, and again on window
 * `focus` — "Connect GitHub" finishes in a *different* tab (this
 * file's own next paragraph), so this tab has no other signal that an
 * installation just completed; a 409 (no installation yet) is treated
 * as "leave the dropdown empty", not an error, since that's simply
 * this org's current, valid state before its first connect. Anything
 * else (401/403/404/500/a network failure) is surfaced directly rather
 * than swallowed the same way — the first version of this caught every
 * failure identically, which made a real backend bug (the route not
 * actually deployed yet, a misconfigured secret, …) indistinguishable
 * from the ordinary "not connected" case live, with nothing on screen
 * to tell the two apart.
 *
 * A sixth gap, same round: "Base branch" defaulted to a guessed
 * `"main"` with no way to tell if that even exists in whichever repo
 * ends up picked — the GitHub App's own `/repos/{owner}/repo}/
 * branches` already knows the real answer (`GET /github/branches`,
 * `list_branches`). It's now a dropdown of that repo's actual
 * branches, fetched fresh whenever `repo` changes (branches are
 * per-repo, not a fixed list like the repos themselves) — `"main"` is
 * still preferred as the default *selection* once the real list comes
 * back, since it's still the common case, but it's no longer typed in
 * blind.
 *
 * Three robustness gaps found live, all fixed here. The root one:
 * "Connect GitHub" used to navigate this same tab to github.com via
 * `window.location.href` — clicking it mid-delivery abandoned the
 * pending /generate/deliver-pr request before it ever got a response
 * (no PR, no error, `busy` just never cleared), and even on its own,
 * leaving the generate/deliver form behind to go install a GitHub App
 * was a worse flow than it needed to be. Fixed at the source: it opens
 * in a new tab (`window.open(..., '_blank')`) instead, so this page
 * never navigates away at all. The other two fixes are defence in
 * depth, kept even though the root cause above is closed, since a
 * right-click "open in current tab" or some other future navigation
 * could still trigger them: "Connect GitHub" stays disabled while a
 * delivery is in flight, and a `pageshow` listener resets to a clean
 * state if this page is ever restored from the browser's bfcache
 * (event.persisted) mid-request — a frozen page's own timers don't run
 * while suspended, so client.ts's request timeout can't reach this case.
 */

import { useEffect, useState } from 'react';

import {
  ApiError,
  deliverPr,
  getGithubBranches,
  getGithubInstallUrl,
  getGithubRepos,
  type GeneratedFile,
} from '../api/client';

interface DeliverAsPrProps {
  script: string;
  tests: string | null;
}

export function DeliverAsPr({ script, tests }: DeliverAsPrProps) {
  const [repo, setRepo] = useState('');
  const [repos, setRepos] = useState<string[] | null>(null);
  const [reposError, setReposError] = useState('');
  const [baseBranch, setBaseBranch] = useState('');
  const [branches, setBranches] = useState<string[] | null>(null);
  const [branchesError, setBranchesError] = useState('');
  const [title, setTitle] = useState('Add generated ETL job');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [prUrl, setPrUrl] = useState('');

  // A different script is a different job: the PR link and any delivery error belong to the
  // one before. Repo, branch and title stay, since they are the user's choices for this page.
  useEffect(() => {
    setPrUrl('');
    setError('');
  }, [script, tests]);

  useEffect(() => {
    function handlePageShow(event: PageTransitionEvent) {
      if (!event.persisted) return;
      // Restored from the browser's bfcache (e.g. pressing Back after
      // "Connect GitHub" navigated away) — any request in flight when
      // the page was left is abandoned, not pending; its own promise
      // may never settle, since the page's timers were suspended too.
      setBusy(false);
      setError('A previous request was interrupted by navigating away — please try again.');
    }
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  useEffect(() => {
    function loadRepos() {
      getGithubRepos()
        .then(({ repos: fetched }) => {
          setRepos(fetched);
          setReposError('');
        })
        .catch((err) => {
          if (err instanceof ApiError && err.status === 409) {
            // No installation yet — this org's ordinary, valid state
            // before its first connect, not a failure to report.
            setRepos(null);
            setReposError('');
            return;
          }
          // Anything else is unexpected — surface it instead of
          // silently falling back to the same "not connected" display
          // a 409 gets, so a real failure here is actually visible.
          setRepos(null);
          setReposError(
            err instanceof ApiError
              ? `Could not load connected repos (${err.message}).`
              : 'Could not load connected repos.',
          );
        });
    }
    loadRepos();
    window.addEventListener('focus', loadRepos);
    return () => window.removeEventListener('focus', loadRepos);
  }, []);

  useEffect(() => {
    if (!repo) {
      setBranches(null);
      setBaseBranch('');
      setBranchesError('');
      return;
    }
    let cancelled = false;
    setBranches(null);
    setBranchesError('');
    getGithubBranches(repo)
      .then(({ branches: fetched }) => {
        if (cancelled) return;
        setBranches(fetched);
        // "main" is still just a default selection, picked from the
        // repo's own real branch list rather than typed in blind —
        // falls back to whatever branch GitHub lists first if there's
        // no "main" (e.g. the repo's default branch is "master" or
        // something else entirely).
        setBaseBranch(fetched.includes('main') ? 'main' : (fetched[0] ?? ''));
      })
      .catch((err) => {
        if (cancelled) return;
        setBranches([]);
        setBranchesError(
          err instanceof ApiError
            ? `Could not load branches (${err.message}).`
            : 'Could not load branches.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [repo]);

  async function connectGithub() {
    setBusy(true);
    setError('');
    try {
      const { install_url } = await getGithubInstallUrl();
      // A new tab, not this one navigating away — see this file's own
      // module docstring for why. noopener/noreferrer: the opened tab
      // is a different origin (github.com) with no reason to hold a
      // reference back to window.opener.
      window.open(install_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start the GitHub connection.');
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    setBusy(true);
    setError('');
    setPrUrl('');
    try {
      const files: GeneratedFile[] = [{ path: 'etl_job.py', content: script }];
      if (tests) files.push({ path: 'test_etl_job.py', content: tests });

      const { pr_url } = await deliverPr({ files, repo, baseBranch, title });
      setPrUrl(pr_url);
    } catch (err) {
      setError(err instanceof ApiError ? (err.reason ?? err.message) : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  const ready = Boolean(repo.trim() && baseBranch.trim() && title.trim());
  const missingFields = [
    !repo.trim() && 'Repo',
    !baseBranch.trim() && 'Base branch',
    !title.trim() && 'PR title',
  ].filter((field): field is string => Boolean(field));

  return (
    <div className="deliver-pr">
      <p className="muted small">
        Don't want to download it yourself?{' '}
        <button className="btn btn--link" disabled={busy} onClick={() => void connectGithub()}>
          Connect GitHub
        </button>{' '}
        once, then deliver this job straight to a repo as a pull request.
      </p>

      {reposError && (
        <p className="alert alert--error" role="alert">
          {reposError}
        </p>
      )}

      {branchesError && (
        <p className="alert alert--error" role="alert">
          {branchesError}
        </p>
      )}

      <div className="field-row">
        <label>
          Repo
          <select
            value={repo}
            onChange={(e) => setRepo(e.target.value)}
            disabled={!repos || repos.length === 0}
          >
            <option value="" disabled>
              {repos === null
                ? 'Connect GitHub first'
                : repos.length === 0
                  ? 'No repositories available'
                  : 'Select a repo…'}
            </option>
            {(repos ?? []).map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <label>
          Base branch
          <select
            value={baseBranch}
            onChange={(e) => setBaseBranch(e.target.value)}
            disabled={!repo || !branches || branches.length === 0}
          >
            <option value="" disabled>
              {!repo
                ? 'Select a repo first'
                : branches === null
                  ? 'Loading branches…'
                  : branches.length === 0
                    ? 'No branches available'
                    : 'Select a branch…'}
            </option>
            {(branches ?? []).map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </label>
        <label>
          PR title
          <input value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
      </div>

      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      {prUrl && (
        <p className="alert alert--info">
          Opened{' '}
          <a href={prUrl} target="_blank" rel="noreferrer">
            {prUrl}
          </a>
        </p>
      )}

      <button className="btn btn--primary" disabled={!ready || busy} onClick={() => void submit()}>
        {busy ? 'Delivering…' : 'Deliver as PR'}
      </button>

      {!ready && missingFields.length > 0 && (
        <p className="muted small">Complete {missingFields.join(', ')} to enable delivery.</p>
      )}
    </div>
  );
}
